<script lang="ts">
  import { AreaChart, Card, CardContent, CardHeader, Page, PieChart } from "framework7-svelte";
  import { translate as tr } from "../../js/i18n";
  import { getSessionsForDay, getStats, type Stats } from "../../js/session.ts";
  import type { SessionStats } from "../../js/settings";

  let stats: Stats = getStats();
  let allSessionDates: Date[] = [];

  const uniqueDateSet = new Set<number>();
  for (const session of stats.sessions) {
    if (!session.date) continue;
    const date = new Date(session.date);
    uniqueDateSet.add(new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime());
  }
  allSessionDates = Array.from(uniqueDateSet).map((ms) => new Date(ms));

  $: lastPeriodSortedDates = [...allSessionDates].sort((a, b) => a.getTime() - b.getTime());
  $: lineChartDatasets = [
    {
      label: tr("stats.duration_minutes"),
      color: "#7AFBFD",
      values: lastPeriodSortedDates.map((d: Date) => {
        const daySessions = getSessionsForDay(stats, d.getFullYear(), d.getMonth(), d.getDate());
        const totalSeconds = daySessions.reduce((acc: number, s: SessionStats) => acc + (s.elapsed || 0), 0);
        return totalSeconds / 60;
      })
    }
  ];

  $: lineChartAxisLabels = lastPeriodSortedDates.map((date) =>
    date.toLocaleDateString("default", {
      month: "short",
      day: "numeric"
    })
  );

  $: distributionData = (() => {
    const buckets = {
      "0-5m": { count: 0, color: "#E0B0FF" },
      "5-10m": { count: 0, color: "#BA55D3" },
      "10-20m": { count: 0, color: "#FF69B4" },
      "20-30m": { count: 0, color: "#A0D2EB" },
      "30m+": { count: 0, color: "#7AFBFD" }
    };

    for (const session of stats.sessions) {
      const minutes = session.elapsed / 60;
      if (minutes < 5) buckets["0-5m"].count++;
      else if (minutes < 10) buckets["5-10m"].count++;
      else if (minutes < 20) buckets["10-20m"].count++;
      else if (minutes < 30) buckets["20-30m"].count++;
      else buckets["30m+"].count++;
    }

    return Object.entries(buckets)
      .filter(([, data]) => data.count > 0)
      .map(([label, data]) => ({
        label: label,
        value: data.count,
        color: data.color
      }));
  })();
</script>

<div class="page-stats-charts">
  <Card>
    <CardHeader>{tr("stats.practiced_over_time")}</CardHeader>
    <CardContent>
      <AreaChart
        lineChart={true}
        axis={true}
        axisLabels={lineChartAxisLabels}
        tooltip={true}
        datasets={lineChartDatasets}
        height={250}
        formatTooltipAxisLabel={(label) => label}
        formatTooltipTotal={(total) => `${total.toFixed(1)} minutes`} />
    </CardContent>
  </Card>

  <Card>
    <CardHeader>{tr("stats.time_spent_sessions")}</CardHeader>
    <CardContent>
      <PieChart
        tooltip={true}
        datasets={distributionData}
        size={120}
        formatTooltip={({ label, value, percentage }) => `${label}: ${value} sessions (${percentage.toFixed(2)}%)`} />
    </CardContent>
  </Card>
</div>

<style>
  :global(.page-stats-charts svg) {
    max-height: 20vh;
  }

  :global(.page-stats-charts .pie-chart svg) {
    max-height: 30vh;
  }
</style>
